/**
 * Where a password-reset email lands first (see `lib/authConfirm`), before
 * `/update-password`. Mirrors `bimgo-ai-web`'s `AuthConfirm` page.
 *
 * The one-time token is spent only when the user presses Continue, never on
 * load: mail scanners load the page and run its JS, but they don't click.
 * `verifyOtp` leaves the recovery session behind in this tab, and the address
 * bar moves on to `/update-password`, whose page finds that session waiting.
 *
 * Exempt from the desktop gate for the same reason `/update-password` is
 * (`App.tsx`): it is opened in a browser by someone who cannot get into the app.
 */
import { useState } from "react";
import { useLocale } from "../i18n/LocaleProvider";
import { clearConfirmLink, readConfirmLink } from "../lib/authConfirm";
import { supabase } from "../lib/supabaseClient";
import { routePath } from "../routes";
import UpdatePassword from "./UpdatePassword";
import "../ui/brand.css";

export default function AuthConfirm() {
  const { t } = useLocale();
  const [link] = useState(readConfirmLink);
  const [verifying, setVerifying] = useState(false);
  const [outcome, setOutcome] = useState<"verified" | "invalid" | null>(link ? null : "invalid");

  if (outcome === "verified") return <UpdatePassword />;

  async function onContinue() {
    if (!link) return;
    setVerifying(true);
    try {
      const { error } = await supabase().auth.verifyOtp({ token_hash: link.tokenHash, type: link.type });
      // Spent either way: a retry would only come back `otp_expired`.
      clearConfirmLink();
      if (error) {
        setOutcome("invalid");
        return;
      }
      window.history.replaceState(null, "", routePath("update-password"));
      setOutcome("verified");
    } catch {
      setOutcome("invalid");
    } finally {
      setVerifying(false);
    }
  }

  return (
    <main className="auth-page">
      <div className="auth-shell">
        <div className="auth-header">
          <img className="auth-logo" src="/bimgo-logo-horizontal-color.png" alt="Bimgo AI" />
        </div>

        <div className="auth-card">
          {outcome === "invalid" ? (
            <div>
              <h1 className="auth-title">{t("updatePassword.invalidTitle")}</h1>
              <p className="auth-message is-error" role="alert">
                {t("updatePassword.invalidDesc")}
              </p>
              <p className="auth-subtitle">{t("updatePassword.invalidRetry")}</p>
            </div>
          ) : (
            <div>
              <h1 className="auth-title">{t("authConfirm.resetTitle")}</h1>
              <p className="auth-subtitle">{t("authConfirm.desc")}</p>
              <div style={{ marginTop: "1rem" }}>
                <button type="button" className="auth-button" onClick={onContinue} disabled={verifying}>
                  {verifying ? t("updatePassword.checking") : t("authConfirm.continue")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
